import constate from 'constate'
import {
  timeOffApi,
  useAddTimeOffRequestMutation,
  useAddTimeOffRequestOnBehalfMutation,
  useGetCountryTimeOffConfigQuery,
  useGetTimeOffBalanceQuery,
  useGetTimeOffRequestsQuery,
} from '@/features/time-off/time-off.api.ts'
import { useEmployeePublicInfo } from '@/common/hooks/useEmployeePublicInfo.tsx'
import { useAuth } from '@/common/hooks/useAuth.ts'
import { useDispatch } from 'react-redux'

export const [EmployeeTimeOffConfigProvider, useEmployeeTimeOffConfig] = constate(({ employeeId, year }: {
  employeeId?: string
  year?: number
}) => {
  const { userProfile } = useAuth()
  const dispatch = useDispatch()
  const [addTimeOffRequest, { isLoading: isAddingSelf }] = useAddTimeOffRequestMutation()
  const [addTimeOffRequestOnBehalf, { isLoading: isAddingOnBehalf }] = useAddTimeOffRequestOnBehalfMutation()

  const { getEmployeePublicInfo } = useEmployeePublicInfo()
  const employee = getEmployeePublicInfo(employeeId)
  const { data: countryConfig, isFetching: isConfigFetching } = useGetCountryTimeOffConfigQuery(
    { country: employee?.country || '' },
    { skip: !employee || !employee.country },
  )
  const { data: balanceResponse, isFetching: isBalanceFetching, refetch: refetchBalances } = useGetTimeOffBalanceQuery({
    employeeId: employee?.employeeId || '',
  }, {
    skip: !employee || !employee.country,
  })
  const timeOffBalances = balanceResponse?.balances
  const attachedPolicy = balanceResponse?.attachedPolicy
  const attachedTimeOffTypes = countryConfig?.policies.find(x => x.id === attachedPolicy?.id)?.timeOffTypes ?? []
  const {
    data: timeOffRequests,
    isFetching: isRequestsFetching,
    refetch: refetchRequests,
  } = useGetTimeOffRequestsQuery({
    employeeId: employee?.employeeId || '',
    year: year, // nullable here
  }, {
    skip: !employee || !employee.country,
  })

  const isOwn = !!employeeId && userProfile?.employeeId === employeeId
  const isFetching = isConfigFetching || isBalanceFetching || isRequestsFetching

  const timeOffTypeNameDict = (timeOffBalances || []).map(x => x.timeOffType).reduce((acc, x) => ({
    ...acc,
    [x.id]: x.name,
  }), {}) as Record<number, string>

  const handleRefreshBalances = () => {
    refetchBalances()
    dispatch(timeOffApi.util.invalidateTags([{ type: 'TimeOffBalanceHistory', id: employee?.employeeId }]))
  }

  return {
    isOwn,
    isFetching,
    employeeId,
    employee,
    countryConfig,
    isConfigFetching,
    timeOffBalances,
    attachedPolicy,
    attachedTimeOffTypes,
    isBalanceFetching,
    refetchBalances: handleRefreshBalances,
    timeOffRequests,
    timeOffTypeNameDict,
    isRequestsFetching,
    refetchRequests,
    addTimeOffRequest: isOwn ? addTimeOffRequest : addTimeOffRequestOnBehalf,
    isAddingRequest: isOwn ? isAddingSelf : isAddingOnBehalf,
  }
})